import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { PRODUCTS, FREE_CARRIAGE_CENTS, CARRIAGE_CENTS, packPctFor } from "@/data/catalog";

const CartContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("dp_cart")) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const [weightLb, setWeightLbState] = useState(() => Number(localStorage.getItem("dp_dog_lb")) || 0);
  const setWeightLb = (lb) => {
    const v = Math.max(0, Math.round(Number(lb) || 0));
    setWeightLbState(v);
    if (v) localStorage.setItem("dp_dog_lb", String(v));
    else localStorage.removeItem("dp_dog_lb");
  };
  const [welcome, setWelcome] = useState({ code: localStorage.getItem("dp_welcome_code") || "", valid: false, reason: "" });

  useEffect(() => {
    localStorage.setItem("dp_cart", JSON.stringify(items));
  }, [items]);

  const applyCode = async (raw) => {
    const code = (raw || "").trim().toUpperCase();
    if (!code) return false;
    const { data } = await axios.get(`${API}/correspondence/code/${encodeURIComponent(code)}`);
    setWelcome({ code, valid: data.valid, reason: data.reason || "", kind: data.kind || "", amountCents: data.amount_cents || 0 });
    if (data.valid) localStorage.setItem("dp_welcome_code", code);
    else localStorage.removeItem("dp_welcome_code");
    return data.valid;
  };
  const removeCode = () => {
    localStorage.removeItem("dp_welcome_code");
    setWelcome({ code: "", valid: false, reason: "" });
  };

  useEffect(() => {
    if (welcome.code) applyCode(welcome.code).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const add = (productId, qty = 1, openDrawer = true) => {
    setItems((prev) => {
      const found = prev.find((i) => i.id === productId);
      if (found) return prev.map((i) => (i.id === productId ? { ...i, qty: i.qty + qty } : i));
      return [...prev, { id: productId, qty }];
    });
    if (openDrawer) setOpen(true);
  };
  const remove = (productId) => setItems((prev) => prev.filter((i) => i.id !== productId));
  const setQty = (productId, qty) =>
    setItems((prev) =>
      qty <= 0 ? prev.filter((i) => i.id !== productId) : prev.map((i) => (i.id === productId ? { ...i, qty } : i))
    );
  const clear = () => setItems([]);

  const detailed = useMemo(
    () =>
      items
        .map((i) => ({ ...i, product: PRODUCTS.find((p) => p.id === i.id) }))
        .filter((i) => i.product),
    [items]
  );
  const totalCents = detailed.reduce((sum, i) => sum + i.product.priceCents * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);
  const jarCount = detailed.filter((i) => i.product.id !== "discovery_dram").reduce((s, i) => s + i.qty, 0);
  const packPct = packPctFor(jarCount);
  const discountCents = Math.floor((totalCents * packPct) / 100);
  const merchandiseCents = totalCents - discountCents;
  const freeShip = welcome.valid && welcome.kind !== "treat";
  const treatCents = welcome.valid && welcome.kind === "treat" ? Math.min(welcome.amountCents, merchandiseCents) : 0;
  const shippingCents = freeShip || totalCents >= FREE_CARRIAGE_CENTS ? 0 : CARRIAGE_CENTS;
  const toFreeCents = freeShip ? 0 : Math.max(0, FREE_CARRIAGE_CENTS - totalCents);
  const pricing = { jarCount, packPct, discountCents, merchandiseCents, shippingCents, toFreeCents, treatCents, welcomeApplied: freeShip, grandCents: merchandiseCents - treatCents + shippingCents };
  const lang = () => (document.documentElement.lang === "fr" ? "fr" : "en");

  const checkout = async () => {
    const { data } = await axios.post(`${API}/payments/checkout`, {
      items: detailed.map((i) => ({ lookup_key: i.product.lookupKey, quantity: i.qty })),
      origin_url: window.location.origin,
      lang: lang(),
      welcome_code: welcome.valid ? welcome.code : null,
    });
    window.location.href = data.checkout_url;
  };

  const checkoutStandingOrder = async (product) => {
    const { data } = await axios.post(`${API}/payments/checkout`, {
      items: [{ lookup_key: product.soLookupKey, quantity: 1 }],
      origin_url: window.location.origin,
      lang: lang(),
    });
    window.location.href = data.checkout_url;
  };

  return (
    <CartContext.Provider
      value={{ items: detailed, count, totalCents, pricing, open, setOpen, add, remove, setQty, clear, checkout, checkoutStandingOrder, welcome, applyCode, removeCode, weightLb, setWeightLb }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
